<script setup>
import Text from "~/components/atoms/Text.vue";
import Input from "~/components/atoms/Input.vue";
import Button from "~/components/atoms/Button.vue";
import ProductPrice from "~/components/molecules/ProductPrice.vue";
import ProductUnit from "~/components/molecules/ProductUnit.vue";
import Circle from "~/components/atoms/Circle.vue";
import CrossIcon from "~/components/icons/CrossIcon.vue";
import ReturnIcon from "~/components/icons/ReturnIcon.vue";
import Disclaimer from "~/components/molecules/Disclaimer.vue";
import {useCartStore} from "~/store/cartStore";

const props = defineProps({
  image: String,
  name: String,
  total: Number,
  unit: String,
  sku: String,
  quantity: Number,
  quantity_info: Number,
  max_qty: Number,
  unit_per_box: Number,
  is_sample: Boolean,
  link: String,
  data_name:String,
  input_name: {
    type: String,
    default: 'quantity'
  },
  noCounter: {
    type: Boolean,
    default: false
  },
  noRemove: {
    type: Boolean,
    default: false
  },
  isRestparti: {
    default: false
  },
  min_volume_to_buy: {
    type: Number,
    default: 1
  },
  cart_multiplicity: {
    type: [Number, String],
    default: 1
  },
  product_type: String,
  tile_bundle: {
    type: Boolean,
    default: false
  },
  supplier_id: {
    type: [Number, String],
    default: null
  }
});

const { t } = useI18n();
const cartStore = useCartStore();

const showZeroTax = computed(() => {
  const rate = Number(cartStore.content?.tax_rate)
  return Number.isFinite(rate) && rate <= 1 && Number(props.total) > 0
})

const zeroTaxPrice = computed(() => Math.round(Number(props.total) / 1.25 * 100) / 100)

const quantityUnit = computed(() => props.unit === 'm2' ? t('product.box') : (props.unit || 'st'));
const multiplicity = computed(() => Number(props.cart_multiplicity) || 1);
const minVolumeToBuy = computed(() => Number(props.min_volume_to_buy) || 1);
const showMultiplicityDisclaimer = computed(() =>
    !props.is_sample && !props.tile_bundle && multiplicity.value > 1
);
const showMinVolumeDisclaimer = computed(() =>
    !props.is_sample && minVolumeToBuy.value > 1 && minVolumeToBuy.value !== multiplicity.value
);
const sampleInformation = computed(() => {
  if (props.product_type === 'Vinylgolv') {
    return t('cart.sample_information_vinyl')
  }

  return t('cart.sample_information')
})
const showBundleSampleDisclaimer = computed(() => props.is_sample && props.tile_bundle)
const minVolumeDisclaimerText = computed(() =>
    t('cart.min_volume_disclaimer', {
      count: minVolumeToBuy.value,
      unit: quantityUnit.value,
    })
);
const multiplicityDisclaimerText = computed(() =>
    t('cart.multiplicity_disclaimer', {
      count: multiplicity.value,
    })
);

const noCounter = computed(() => ['change_order', 'cancel_order'].includes(props.data_name) ? true : props.noCounter);
const emits = defineEmits(['plus', 'minus', 'update', 'remove', 'click']);

let timer;
const removeTimeout = 2000;
const isRemoving = ref(false);

const removeItem = () => {
  emits('remove');
  return;
  if (timer) {
    isRemoving.value = false
    clearTimeout(timer)
    timer = null
  } else {
    isRemoving.value = true
    timer = setTimeout(() => {
      emits('remove');
    }, removeTimeout);
  }
};

// const increaseQuantity = () => {
//   if (qty.value < props.max_qty) {
//     qty.value++;
//     emits('update:quantity', qty.value);
//   }
// };
//
// const decreaseQuantity = () => {
//   if (qty.value > 0) {
//     qty.value--;
//     emits('update:quantity', qty.value);
//   }
// };
//
// const qty = ref(props.quantity);
//
// const change = (e) => {
//   if (props.max_qty) {
//     if (qty.value > props.max_qty) {
//       qty.value = props.max_qty
//     }
//   }
//   emits('update', e);
// };

const hideQuantityInput = computed(() => Number(props.supplier_id) === 196)

const qty = computed(() => {
  return props.quantity;
});

const change = (value) => {
  const next = parseInt(value, 10)

  if (!Number.isFinite(next) || next === Number(props.quantity)) {
    return
  }

  emits('update', next);
};

</script>
<template>
  <div class="product">
    <div class="row full-width g-16 stretch">
      <div class="image g-8" @click.stop>
        <img :src="props.image"/>
        <Text text-alignment="center" class="sku">{{ props.sku }}</Text>
      </div>
<!--      <div v-if="!isRemoving" class="g-8 space-between" @click.stop.prevent>-->
      <div v-if="!isRemoving" class="g-8 space-between" @click.stop>
        <div class="row full-width space-between flex-start">
          <nuxt-link class="name" :to="sku.startsWith('HCS') ? false : link" @click="$emit('click')">
            <Text class="product-name" tag="b">{{ props.name }}</Text>
          </nuxt-link>
          <slot name="additional_product_info"/>
          <div class="remove no-flex flex-end" v-if="!noRemove">
            <CrossIcon v-if="!isRemoving" width="22px" height="22px" @click.prevent.stop="removeItem"
                       class="clickable"/>
          </div>
        </div>
        <slot/>
        <div class="row full-width">
          <div class="row g-8 the-same-gap full-width flex-end" :class="[unit]">
            <template v-if="!props.sku.startsWith('HCS')">
              <template v-if="noCounter">
                <Text v-if="!isRestparti">{{ props.quantity }} {{ props.unit === 'm2' ? 'box' : 'st' }}</Text>
                <input v-if="props.data_name === 'cancel_order'" type="hidden" :name="input_name" :value="props.quantity"/>
              </template>
              <template v-else>
                <div v-if="props.quantity_info" class="row quantity-info g-4">
                  <p>{{ props.quantity_info }} </p>
                  <div class="row g-4 static" v-if="props.unit === 'm2' && props.unit_per_box">
                    <Text>box</Text>
                  </div>
                  <div class="row g-4" v-else-if="props.unit === 'st' && !props.is_sample && !isRestparti">
                    <Text>
                      <ProductUnit unit="st" :sup="true"/>
                    </Text>
                  </div>
                </div>
                <!--              <div class="row" v-if="!props.is_sample">-->
                <!--&lt;!&ndash;                <Button type="minus" @click.stop="$emit('minus')"/>&ndash;&gt;-->
                <!--                <Button type="minus" @click.stop="decreaseQuantity"/>-->
                <!--                &lt;!&ndash;                <Input :name="input_name" width="32px" @change="change" type="number" min="1"&ndash;&gt;-->
                <!--                &lt;!&ndash;                       :key="props.quantity"&ndash;&gt;-->
                <!--                &lt;!&ndash;                       max="9999" v-model="qty"/>&ndash;&gt;-->
                <!--                <Input :name="input_name" width="32px" @change="change" type="number" min="1"-->
                <!--                       :key="props.quantity"-->
                <!--                       max="9999" v-model="qty"/>-->
                <!--&lt;!&ndash;                <Button type="plus" @click.stop="$emit('plus')"/>&ndash;&gt;-->
                <!--                <Button type="plus" @click.stop="increaseQuantity" />-->
                <!--              </div>-->

                <div class="row" v-if="!props.is_sample && !hideQuantityInput">
                  <Button type="minus" @mousedown.prevent @click.stop="$emit('minus')"/>
                  <Input :name="input_name" width="32px" @change="change" type="number" min="1"
                         :key="props.quantity"
                         max="9999" :model-value="qty"/>
                  <Button type="plus" @mousedown.prevent @click.stop="$emit('plus')"/>
                </div>
                <div v-else-if="props.is_sample && !props.tile_bundle">
                  <Text style="font-size: 12px;">{{ sampleInformation }}</Text>
                </div>
              </template>
              <template v-if="!noCounter && !hideQuantityInput">
                <div class="row g-4 static" v-if="props.unit === 'm2' && props.unit_per_box">
                  <Text>box</Text>
                  <Text v-if="!props.quantity_info" class="unit-calculator">=
                    {{ Math.round(props.unit_per_box * props.quantity * 100) / 100 }}
                    <ProductUnit unit="m2" :sup="true"/>
                  </Text>
                </div>
                <div class="row g-4" v-else-if="props.unit === 'st' && !props.is_sample && !isRestparti">
                  <Text>
                    <ProductUnit unit="st" :sup="true"/>
                  </Text>
                </div>
              </template>
            </template>
            <div class="flex-end left-auto" :class="showZeroTax ? 'zero-tax-stack' : 'row g-8'">
              <template v-if="props.total && showZeroTax">
                <ProductPrice type="old" :price="props.total"/>
                <ProductPrice :red="true" :price="zeroTaxPrice"/>
                <Text type="red" class="zero-tax-label">(0% {{ $t('product.taxes') }})</Text>
              </template>
              <template v-else-if="props.total">
                <ProductPrice :price="props.total"/>
              </template>
              <template v-else>
                <slot name="total"/>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="remove-callback flex-center" v-if="isRemoving">
        <Circle class="circle"/>

        <div class="row g-8" style="font-size: 12px;">
          <Text>Artikeln borttagen</Text>
          <Text tag="strong" class="underline clickable" @click.stop="removeItem">Ångra</Text>
        </div>

        <div class="return clickable" @click.stop="removeItem">
          <ReturnIcon/>
        </div>
      </div>
    </div>

    <Disclaimer
        v-if="showMultiplicityDisclaimer"
        class="product-disclaimer"
        :text="multiplicityDisclaimerText"
        @click.stop
    />
    <Disclaimer
        v-if="showBundleSampleDisclaimer"
        class="product-disclaimer"
        :text="t('product.about_sample_bundle')"
        @click.stop
    />
    <Disclaimer
        v-if="showMinVolumeDisclaimer"
        class="product-disclaimer"
        :text="minVolumeDisclaimerText"
        @click.stop
    />

    <slot name="under" v-if="$slots['under']"/>
  </div>
</template>

<style scoped lang="scss">
.name {
  max-width: 90%;
}

.m2 {
  @include mobile {
    margin-bottom: 20px;
  }
}

.unit-calculator {
  @include mobile {
    position: absolute;
    left: 0;
    margin-top: 50px;
  }
}

.remove-callback {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);

  .circle {
    left: 0;
    top: 50%;
    transform: translateY(-105%) scale(.17);
  }

  .return {
    position: absolute;
    top: 0;
    right: 0;
  }
}

.product {
  padding-bottom: 16px;
  border-bottom: 1px solid $border-grey;

  &:last-child {
    border-bottom: 0;
  }
}

.product-disclaimer {
  width: 100%;
  margin-top: 8px;
}

.image {
  max-width: 71px;

  .sku {
    color: $black;
    font-size: 12px;
  }

  img {
    width: 100%;
  }
}

.product-name {
  max-width: 80%;
}

.zero-tax-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0;
  line-height: 1.2;
}

.zero-tax-label {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.price {
  font-size: 14px;
  font-weight: 500;
}

button {
  &.minus, &.plus {
    width: 26px;
    height: 26px;
    min-width: 26px;
    min-height: 26px;
    max-width: 26px;
    max-height: 26px;
  }
}

.remove {
}

.quantity-info {
  min-width: 54px;
}

</style>
